import type { ReactNode } from 'react';
import type { FlagName } from '../store/types';
import { useFeatureFlag } from './useFeatureFlag';
import { useFeatureFlagVariant } from './useFeatureFlagVariant';

interface BooleanGuardProps {
  flag: FlagName;
  variant?: never;
  fallback?: ReactNode;
  children: ReactNode;
}

interface VariantGuardProps {
  flag: FlagName;
  variant: string;
  fallback?: ReactNode;
  children: ReactNode;
}

type FeatureFlagGuardProps = BooleanGuardProps | VariantGuardProps;

export function FeatureFlagGuard({ flag, variant, fallback = null, children }: FeatureFlagGuardProps) {
  if (variant !== undefined) {
    return (
      <VariantGate flag={flag} variant={variant} fallback={fallback}>
        {children}
      </VariantGate>
    );
  }

  return (
    <BooleanGate flag={flag} fallback={fallback}>
      {children}
    </BooleanGate>
  );
}

function BooleanGate({ flag, fallback, children }: { flag: FlagName; fallback: ReactNode; children: ReactNode }) {
  const enabled = useFeatureFlag(flag);
  return enabled ? children : fallback;
}

function VariantGate({ flag, variant, fallback, children }: { flag: FlagName; variant: string; fallback: ReactNode; children: ReactNode }) {
  const current = useFeatureFlagVariant(flag);
  return current === variant ? children : fallback;
}
